/**
 * DealItemsSummary - Resumo dos itens do deal
 * Tabela compacta com edição inline e totalizadores
 *
 * Versão: 3.1.0
 * - Adicionada prop `showTotals` para ocultar os totalizadores quando o componente
 *   é reusado em telas que já exibem KPIs próprios (ex.: modal de detalhes da proposta).
 *
 * Versão: 3.0.0
 * - Adicionadas colunas Unidade, Mensalidade e Prazo na tabela de itens
 * - Itens são separados visualmente em "Produtos" e "Serviços" quando ambos existem
 * - Novos cards de totais: Total Mensal (MRR) e Total Vigência (somente quando há serviço recorrente)
 * - Reuso 100% dos utilitários em src/lib/crm/recurringServiceUtils.ts
 *
 * Versão 2.0.0: Suporte a desconto em % e valor fixo, toggle inline por item.
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Package,
  Wrench,
  Trash2,
  ShoppingCart,
  Percent,
  Calculator,
  Plus,
  RefreshCw,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/currency-utils';
import { InlineEditableField } from './InlineEditableField';
import {
  isRecurringService,
  formatRecurrenceLabel,
  formatTermLabel,
  calculateRecurringTotal,
  type RecurrenceInterval,
  type TermType,
  type TermUnit,
} from '@/lib/crm/recurringServiceUtils';

/** Tipo do modo de desconto: percentual ou valor fixo */
type DiscountMode = 'percent' | 'fixed';

// Interface para item do deal
interface DealItem {
  id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number | null;
  discount_fixed: number | null;
  /**
   * Modo de desconto explícito (opcional). Quando informado, força o toggle
   * %/R$ a abrir nesse modo, independentemente dos valores numéricos. Usado
   * pelo EditSubscriptionModal para hidratar fielmente o tipo persistido em
   * financeiro_cobranca_itens.metadata.desconto_tipo.
   */
  discount_mode?: DiscountMode | null;
  total: number;
  product?: {
    id: string;
    name: string;
    sku: string | null;
    product_type: 'product' | 'service';
    unit_type: string;
    recurring_price?: number | null;
    recurrence_interval?: RecurrenceInterval | null;
    term_type?: TermType | null;
    term_quantity?: number | null;
    term_unit?: TermUnit | null;
  };
}


// Props do componente
interface DealItemsSummaryProps {
  items: DealItem[];
  currency: string;
  readOnly: boolean;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  /** Callback atualizado para enviar ambos os valores de desconto */
  onUpdateDiscount: (itemId: string, percent: number, fixed: number) => void;
  onRemove: (itemId: string) => void;
  /** Callback para abrir o modal de catálogo (opcional) */
  onOpenCatalog?: () => void;
  /**
   * Variante de exibição:
   * - 'deal' (default): comportamento original na aba Produtos do Negócio
   * - 'subscription': layout enxuto para o modal Nova Recorrência
   */
  variant?: 'deal' | 'subscription';
  /**
   * IDs de produtos "órfãos" — presentes no deal mas não vinculados à
   * workspace atual do deal (após uma movimentação cross-workspace).
   * Quando informado, exibe um aviso amarelo no topo da tabela.
   * v2.0.0 — 2026-07-11
   */
  orphanProductIds?: Set<string>;
  /**
   * Exibe ou oculta a faixa de totalizadores (Subtotal, Descontos, Total, MRR).
   * Útil quando o componente é reusado em telas que já possuem KPIs próprios,
   * como o modal de detalhes da proposta.
   * v3.1.0 — 2026-08-11
   */
  showTotals?: boolean;
}

/** Determina modo de desconto ativo baseado nos valores do item.
 * Prioriza `discount_mode` quando informado (hidratação fiel de metadata);
 * caso contrário, deduz pelos valores numéricos preservando compatibilidade. */
function getDiscountMode(item: DealItem): DiscountMode {
  if (item.discount_mode === 'percent' || item.discount_mode === 'fixed') {
    return item.discount_mode;
  }
  if (item.discount_fixed && item.discount_fixed > 0) return 'fixed';
  return 'percent';
}

/** Calcula valor total de desconto de um item (% + fixo) */
function calcDiscountAmount(item: DealItem): number {
  const itemSubtotal = item.unit_price * item.quantity;
  const percentDiscount = item.discount_percent ? itemSubtotal * (item.discount_percent / 100) : 0;
  const fixedDiscount = item.discount_fixed || 0;
  return percentDiscount + fixedDiscount;
}

/**
 * Normaliza valor recorrente para ciclo mensal (MRR).
 * Helper local — recurringServiceUtils.ts só calcula o total da vigência.
 */
const MONTHLY_FACTOR: Record<RecurrenceInterval, number> = {
  semanal: 4.3333,
  quinzenal: 2,
  mensal: 1,
  bimestral: 1 / 2,
  trimestral: 1 / 3,
  semestral: 1 / 6,
  anual: 1 / 12,
  bienal: 1 / 24,
};

function normalizeToMonthly(price: number, recurrence?: RecurrenceInterval | null): number {
  if (!recurrence) return 0;
  return price * (MONTHLY_FACTOR[recurrence] ?? 0);
}

/** Toggle compacto para alternar entre modo % e R$ */
function DiscountModeToggle({
  mode,
  onToggle,
}: {
  mode: DiscountMode;
  onToggle: (newMode: DiscountMode) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 overflow-hidden h-6">
      <button
        type="button"
        onClick={() => onToggle('percent')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'percent'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
        )}
        title="Desconto em percentual"
      >
        %
      </button>
      <button
        type="button"
        onClick={() => onToggle('fixed')}
        className={cn(
          'px-1.5 text-[10px] font-medium transition-colors',
          mode === 'fixed'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
        )}
        title="Desconto em valor fixo"
      >
        R$
      </button>
    </div>
  );
}

/**
 * Card de totalização — design "Underline accent" (clean/minimalista).
 * Fundo neutro (card) + borda fina + faixa colorida fina no topo (2px)
 * indicando a semântica. Apenas o "Total" recebe destaque (accent primary +
 * leve ring); demais variantes ficam neutras para reduzir poluição visual.
 */
function TotalCard({
  icon: Icon,
  label,
  value,
  variant = 'default',
  footer,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  variant?: 'default' | 'discount' | 'total' | 'recurring';
  footer?: React.ReactNode;
}) {
  // Cor da faixa superior (2px) — única fonte de cor no card
  const topBorder =
    variant === 'discount'
      ? 'border-t-orange-400'
      : variant === 'total'
      ? 'border-t-primary'
      : 'border-t-border';

  // Realce extra somente para o card "Total"
  const totalRing = variant === 'total' ? 'shadow-sm ring-1 ring-primary/10' : 'shadow-sm';

  return (
    <div
      className={cn(
        'flex-1 min-w-[145px] p-3 rounded-lg bg-card border border-border border-t-2',
        topBorder,
        totalRing
      )}
    >
      <div className="flex items-center justify-between mb-1">
        <span
          className={cn(
            'text-[9px] font-semibold uppercase tracking-wider',
            variant === 'total' ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          {label}
        </span>
        <Icon
          className={cn(
            'h-3 w-3',
            variant === 'discount'
              ? 'text-orange-500/70'
              : variant === 'total'
              ? 'text-primary/70'
              : 'text-muted-foreground/60'
          )}
        />
      </div>
      <p
        className={cn(
          variant === 'total' ? 'text-xl font-extrabold' : 'text-lg font-bold',
          variant === 'discount' ? 'text-orange-600' : 'text-foreground'
        )}
      >
        {value}
      </p>
      {footer && <div className="mt-1 text-[9px] text-muted-foreground">{footer}</div>}
    </div>
  );
}

/** Helper: verifica se um item é serviço recorrente */
function itemIsRecurring(item: DealItem): boolean {
  return isRecurringService(item.product?.product_type, item.product?.unit_type);
}

/**
 * Sub-tabela de itens (renderizada uma ou duas vezes — produtos e serviços)
 */
function ItemsTable({
  items,
  currency,
  readOnly,
  getItemDiscountMode,
  handleToggleDiscountMode,
  onUpdateQuantity,
  onUpdateDiscount,
  onRemove,
  hideUnitColumn = false,
  hidePriceColumn = false,
  productLabel = 'Produto',
}: {
  items: DealItem[];
  currency: string;
  readOnly: boolean;
  getItemDiscountMode: (item: DealItem) => DiscountMode;
  handleToggleDiscountMode: (itemId: string, newMode: DiscountMode) => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onUpdateDiscount: (itemId: string, percent: number, fixed: number) => void;
  onRemove: (itemId: string) => void;
  /** Oculta a coluna "Unidade" (usado no modal de recorrência) */
  hideUnitColumn?: boolean;
  /** Oculta a coluna "Preço" unitário (usado no modal de recorrência) */
  hidePriceColumn?: boolean;
  /** Rótulo da coluna principal (default "Produto"; "Serviço" no modal de recorrência) */
  productLabel?: string;
}) {
  // v1.1.0: Colunas Mensalidade/Prazo só fazem sentido se houver ao menos
  // um item recorrente na tabela. Para a seção "Produtos" (sem recorrentes),
  // as colunas ficam ocultas; na seção "Serviços" elas aparecem.
  const showRecurringColumns = items.some(itemIsRecurring);
  // Ajusta min-width quando colunas Unidade/Preço são suprimidas, para o
  // nome do serviço ganhar mais espaço sem rolagem horizontal indevida.
  const compact = hideUnitColumn || hidePriceColumn;
  const minWidth = showRecurringColumns
    ? (compact ? 'min-w-[720px]' : 'min-w-[900px]')
    : (compact ? 'min-w-[520px]' : 'min-w-[680px]');

  return (
    <div className="border border-border/50 rounded-lg">
      <ScrollArea
        className="max-h-[280px] w-full rounded-lg"
        axis="both"
        reserveScrollbarSpace
      >
        <div className={minWidth}>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="text-xs h-8">{productLabel}</TableHead>
                {!hideUnitColumn && (
                  <TableHead className="text-xs h-8 w-20 text-center">Unidade</TableHead>
                )}
                <TableHead className="text-xs h-8 w-16 text-center">Qtd</TableHead>
                {!hidePriceColumn && (
                  <TableHead className="text-xs h-8 w-24 text-right">Preço</TableHead>
                )}
                {showRecurringColumns && (
                  <>
                    <TableHead className="text-xs h-8 w-36 text-right">Mensalidade</TableHead>
                    <TableHead className="text-xs h-8 w-28 text-center">Prazo</TableHead>
                  </>
                )}
                <TableHead className="text-xs h-8 w-28 text-center">Desconto</TableHead>
                <TableHead className="text-xs h-8 w-24 text-right">Total</TableHead>
                {!readOnly && <TableHead className="text-xs h-8 w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const TypeIcon = item.product?.product_type === 'service' ? Wrench : Package;
                const itemSubtotal = item.unit_price * item.quantity;
                const discountAmount = calcDiscountAmount(item);
                const itemTotal = itemSubtotal - discountAmount;
                const currentMode = getItemDiscountMode(item);
                const recurring = itemIsRecurring(item);
                const monthlyLabel = recurring && item.product?.recurring_price
                  ? `${formatCurrency(item.product.recurring_price, { currency })} / ${formatRecurrenceLabel(item.product.recurrence_interval)}`
                  : null;
                const termLabel = recurring
                  ? formatTermLabel(item.product?.term_type, item.product?.term_quantity, item.product?.term_unit)
                  : null;

                return (
                  <TableRow key={item.id} className="group">
                    {/* Produto */}
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <TypeIcon
                          className={cn(
                            'h-3.5 w-3.5 shrink-0',
                            item.product?.product_type === 'service'
                              ? 'text-blue-500'
                              : 'text-amber-500'
                          )}
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">
                            {item.product?.name || 'Produto'}
                          </p>
                          {item.product?.sku && (
                            <p className="text-[10px] text-muted-foreground">
                              {item.product.sku}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    {/* Unidade (opcional) */}
                    {!hideUnitColumn && (
                      <TableCell className="py-2 text-center">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">
                          {item.product?.unit_type || '—'}
                        </Badge>
                      </TableCell>
                    )}

                    {/* Quantidade */}
                    <TableCell className="py-2">
                      {readOnly ? (
                        <span className="text-xs text-center block">{item.quantity}</span>
                      ) : (
                        <InlineEditableField
                          value={item.quantity}
                          onChange={(qty) => onUpdateQuantity(item.id, qty)}
                          min={1}
                          allowDecimals={false}
                          className="h-7 w-16 text-xs text-center mx-auto"
                        />
                      )}
                    </TableCell>

                    {/* Preço Unitário (opcional) */}
                    {!hidePriceColumn && (
                      <TableCell className="py-2 text-right">
                        <span className="text-xs">
                          {formatCurrency(item.unit_price, { currency })}
                        </span>
                      </TableCell>
                    )}

                    {/* Mensalidade + Prazo (somente quando tabela contém recorrentes) */}
                    {showRecurringColumns && (
                      <>
                        <TableCell className="py-2 text-right">
                          {monthlyLabel ? (
                            <span className="text-xs text-primary inline-flex items-center gap-1 justify-end">
                              <RefreshCw className="h-3 w-3" />
                              {monthlyLabel}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          {termLabel ? (
                            <span className="text-xs">{termLabel}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </>
                    )}

                    {/* Desconto */}
                    <TableCell className="py-2">
                      {readOnly ? (
                        <span className="text-xs text-center block">
                          {item.discount_percent && item.discount_percent > 0
                            ? `${item.discount_percent}%`
                            : item.discount_fixed && item.discount_fixed > 0
                              ? formatCurrency(item.discount_fixed, { currency })
                              : '-'}
                        </span>
                      ) : (
                        <div className="flex items-center gap-1 justify-center">
                          <DiscountModeToggle
                            mode={currentMode}
                            onToggle={(newMode) => handleToggleDiscountMode(item.id, newMode)}
                          />
                          <InlineEditableField
                            key={`${item.id}-${currentMode}`}
                            value={
                              currentMode === 'percent'
                                ? (item.discount_percent || 0)
                                : (item.discount_fixed || 0)
                            }
                            onChange={(val) => {
                              if (currentMode === 'percent') {
                                onUpdateDiscount(item.id, val, 0);
                              } else {
                                onUpdateDiscount(item.id, 0, val);
                              }
                            }}
                            min={0}
                            max={currentMode === 'percent' ? 100 : itemSubtotal}
                            placeholder="-"
                            className="h-7 w-16 text-xs text-center"
                          />
                        </div>
                      )}
                    </TableCell>

                    {/* Total */}
                    <TableCell className="py-2 text-right">
                      <span className="text-xs font-medium">
                        {formatCurrency(itemTotal, { currency })}
                      </span>
                    </TableCell>

                    {/* Ações */}
                    {!readOnly && (
                      <TableCell className="py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => onRemove(item.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}

/**
 * Resumo dos itens do deal com totalizadores
 */
export function DealItemsSummary({
  items,
  currency,
  readOnly,
  onUpdateQuantity,
  onUpdateDiscount,
  onRemove,
  onOpenCatalog,
  variant = 'deal',
  orphanProductIds,
  showTotals = true,
}: DealItemsSummaryProps) {
  const isSubscription = variant === 'subscription';
  // Estado local do modo de desconto por item
  const [discountModes, setDiscountModes] = useState<Record<string, DiscountMode>>({});

  const getItemDiscountMode = useCallback(
    (item: DealItem): DiscountMode => discountModes[item.id] || getDiscountMode(item),
    [discountModes]
  );

  const handleToggleDiscountMode = useCallback(
    (itemId: string, newMode: DiscountMode) => {
      setDiscountModes((prev) => ({ ...prev, [itemId]: newMode }));
      onUpdateDiscount(itemId, 0, 0);
    },
    [onUpdateDiscount]
  );

  // Separar itens por tipo
  const { productItems, serviceItems } = useMemo(() => {
    const p: DealItem[] = [];
    const s: DealItem[] = [];
    items.forEach((it) => {
      if (it.product?.product_type === 'service') s.push(it);
      else p.push(it);
    });
    return { productItems: p, serviceItems: s };
  }, [items]);

  // Totais base
  const subtotal = items.reduce((acc, item) => acc + item.unit_price * item.quantity, 0);
  const totalDiscounts = items.reduce((acc, item) => acc + calcDiscountAmount(item), 0);
  const total = subtotal - totalDiscounts;

  // Métricas de recorrência (MRR + Vigência)
  // Aplica o desconto da linha:
  //   - discount_percent reduz a mensalidade (afeta MRR e todos os ciclos da vigência)
  //   - discount_fixed é abatido uma única vez da vigência por unidade (não afeta MRR)
  const recurringMetrics = useMemo(() => {
    let mrr = 0;
    let lifetime = 0;
    let hasIndeterminate = false;
    let hasRecurring = false;

    items.forEach((item) => {
      if (!itemIsRecurring(item)) return;
      hasRecurring = true;
      const priceBruto = item.product?.recurring_price || 0;
      const qty = item.quantity || 0;
      const pctOff = item.discount_percent || 0;
      const fixedOff = item.discount_fixed || 0;
      const mensalidadeLiq = priceBruto * (1 - pctOff / 100);

      mrr += normalizeToMonthly(mensalidadeLiq, item.product?.recurrence_interval) * qty;

      const oneTotal = calculateRecurringTotal({
        recurring_price: mensalidadeLiq,
        recurrence_interval: item.product?.recurrence_interval ?? null,
        term_type: item.product?.term_type ?? null,
        term_quantity: item.product?.term_quantity ?? null,
        term_unit: item.product?.term_unit ?? null,
      });
      if (oneTotal !== null) {
        // Desconto fixo abate uma vez por unidade; nunca deixa negativo
        lifetime += Math.max(0, oneTotal * qty - fixedOff * qty);
      } else if (item.product?.term_type === 'indeterminado') {
        hasIndeterminate = true;
      }
    });

    return { mrr, lifetime, hasIndeterminate, hasRecurring };
  }, [items]);

  // v2.0.0: itens órfãos (produto fora da workspace atual do deal)
  // IMPORTANTE: declarado ANTES de qualquer early return para não violar
  // as Rules of Hooks (contagem de hooks estável entre renders).
  const orphanItems = useMemo(() => {
    if (!orphanProductIds || orphanProductIds.size === 0) return [] as DealItem[];
    return items.filter((it) => orphanProductIds.has(it.product_id));
  }, [items, orphanProductIds]);

  // Estado vazio
  if (items.length === 0) {
    return (
      <div className="p-8 text-center border border-dashed border-border/50 rounded-lg bg-muted/20">
        <ShoppingCart className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-medium text-muted-foreground mb-1">
          Nenhum produto adicionado ao negócio
        </p>
        <p className="text-xs text-muted-foreground mb-4">
          Adicione produtos ou serviços para compor este negócio
        </p>
        {onOpenCatalog && !readOnly && (
          <Button variant="outline" onClick={onOpenCatalog}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Produto
          </Button>
        )}
      </div>
    );
  }

  // Mostrar agrupamento somente quando há os dois tipos
  // (na variante 'subscription' nunca agrupa — tudo é serviço recorrente)
  const showGroups = !isSubscription && productItems.length > 0 && serviceItems.length > 0;

  const tableProps = {
    currency,
    readOnly,
    getItemDiscountMode,
    handleToggleDiscountMode,
    onUpdateQuantity,
    onUpdateDiscount,
    onRemove,
    // Variante de recorrência: oculta Unidade/Preço e renomeia coluna principal
    hideUnitColumn: isSubscription,
    hidePriceColumn: isSubscription,
    productLabel: isSubscription ? 'Serviço' : 'Produto',
  };


  return (
    <div className="space-y-3">
      {/* Aviso: itens órfãos após movimentação cross-workspace */}
      {!isSubscription && orphanItems.length > 0 && (
        <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5 text-xs">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-amber-900 dark:text-amber-100">
            <p className="font-medium">
              {orphanItems.length} {orphanItems.length === 1 ? 'item pertence' : 'itens pertencem'} a
              {' '}produtos não vinculados à área de trabalho atual do negócio.
            </p>
            <p className="mt-0.5 text-amber-800/80 dark:text-amber-200/80">
              Eles continuam preservados aqui (histórico e valor intactos), mas não aparecerão no catálogo
              ao adicionar novos itens. Para reincluí-los, vincule o produto a esta área ou remova-os.
            </p>
          </div>
        </div>
      )}

      {/* Cabeçalho — suprimido na variante 'subscription' */}
      {!isSubscription && (
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <ShoppingCart className="h-3.5 w-3.5" />
            Itens do Negócio ({items.length})
          </h4>
          {onOpenCatalog && !readOnly && (
            <Button variant="ghost" size="sm" onClick={onOpenCatalog} className="h-7 text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" />
              Adicionar
            </Button>
          )}
        </div>
      )}


      {/* Tabelas (uma ou duas conforme houver agrupamento) */}
      {showGroups ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Package className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Produtos ({productItems.length})
              </span>
            </div>
            <ItemsTable items={productItems} {...tableProps} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Wrench className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Serviços ({serviceItems.length})
              </span>
            </div>
            <ItemsTable items={serviceItems} {...tableProps} />
          </div>
        </div>
      ) : (
        <ItemsTable items={items} {...tableProps} />
      )}

      {/* Totalizadores — podem ser suprimidos quando o componente é reusado em
          telas que já exibem os KPIs no cabeçalho (ex.: modal de detalhes da proposta) */}
      {showTotals && (
        <div className="flex flex-wrap gap-3">
          <TotalCard
            icon={ShoppingCart}
            label="Subtotal"
            value={formatCurrency(subtotal, { currency })}
            variant="default"
          />
          <TotalCard
            icon={Percent}
            label="Descontos"
            value={`-${formatCurrency(totalDiscounts, { currency })}`}
            variant="discount"
          />
          <TotalCard
            icon={Calculator}
            label="Total"
            value={formatCurrency(total, { currency })}
            variant="total"
          />

          {/* Cards de recorrência — só aparecem se há ao menos um serviço recorrente */}
          {recurringMetrics.hasRecurring && (
            <>
              <TotalCard
                icon={RefreshCw}
                label="Total Mensal (MRR)"
                value={formatCurrency(recurringMetrics.mrr, { currency })}
                variant="recurring"
              />
              <TotalCard
                icon={CalendarClock}
                label="Tot. Vigência Contrato"
                value={formatCurrency(recurringMetrics.lifetime, { currency })}
                variant="recurring"
                footer={
                  recurringMetrics.hasIndeterminate
                    ? '+ contratos por prazo indeterminado'
                    : undefined
                }
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
